import {
  PartType,
  PART_TYPE_REGEX_MAP,
  MODULE_LOCATIONS_FINAL_LIST,
  MODULE_LOCATIONS_LIST,
} from "@constants";

import { JsonFormControl, ConditionalLogic } from "./types";

/**
 * This file holds "factory functions" to create form control objects to handle
 * various kinds of shared inputs. For example, most step entry forms have a
 * optional "comments" field, so moving the logic for that form control
 * element out of each individual form control and into a centralized location
 * keeps the code cleaner.
 */

/**
 * Makes an optional comments field.
 * @returns {JsonFormControl}
 */
export function makeCommentsControl(): JsonFormControl {
  return {
    label: "Comments",
    inputType: "text",
    fieldName: "comments",
    defaultValue: "",
  };
}

/**
 * Creates a select control based on a handful of parameters.
 * @param label User Facing label for the data.
 * @param fieldName DB label for the data.
 * @param options list of options to be displayed
 * @param invalidMessage optional message to display during validation.
 * @returns {JsonFormControl}
 */
export function makeSelectControl(
  label: string,
  fieldName: string,
  options: (string | { value: string; label: string })[],
  invalidMessage: string | undefined,
  defaultValue = "",
  conditionalLogic?: ConditionalLogic[] | undefined,
  dependency?: string | undefined,
): JsonFormControl {
  const requiredConfig =
    invalidMessage === undefined || invalidMessage === ""
      ? {}
      : {
          config: {
            required: invalidMessage,
            minLength: { value: 1, message: invalidMessage },
          },
        };
  return {
    label,
    inputType: "select",
    fieldName,
    options: options.map((opt) =>
      typeof opt === "string" ? { value: opt, label: opt } : opt,
    ),
    defaultValue: defaultValue,
    conditionalLogic: conditionalLogic,
    dependency: dependency,
    ...requiredConfig,
  };
}

export function makeNumberControl(
  label: string,
  fieldName: string,
  invalidMessage: string | undefined,
  defaultValue: number | undefined,
  conditionalLogic?: ConditionalLogic[] | undefined,
  dependency?: string | undefined,
): JsonFormControl {
  const requiredConfig =
    invalidMessage === undefined || invalidMessage === ""
      ? {}
      : {
          config: {
            required: invalidMessage,
            minLength: { value: 1, message: invalidMessage },
          },
        };
  return {
    label,
    inputType: "number",
    fieldName,
    defaultValue: defaultValue,
    conditionalLogic: conditionalLogic,
    dependency: dependency,
    ...requiredConfig,
  };
}

/**
 * Makes a form control object that gives a "passed" or "failed" option from
 * a dropdown menu.
 * @param {string} label The UI facing label entered by the user.
 * @param {string} invalidMessage Validation message.
 * @returns {JsonFormControl}
 */
export function makePassFailControl(
  label: string,
  invalidMessage = 'Select either "passed" or "failed".',
): JsonFormControl {
  return makeSelectControl(
    label,
    "passed",
    [
      { value: "true", label: "Passed" },
      { value: "false", label: "Failed" },
    ],
    invalidMessage,
  );
}

/**
 * Makes a form control to create a dropdown for the number of stumps on a
 * module.
 * @param label The label for the form field.
 * @param fieldName The object name for the field.
 * @param numberOfStumps the max number of stumps that can be selected. (default 2)
 * @param invalidMessage The message displayed when the entry is invalid.
 */
export function makeStumpSelectControl(
  label: string,
  fieldName: string,
  numberOfStumps = 2,
  invalidMessage = "Select a number of stumps.",
): JsonFormControl {
  return makeSelectControl(
    label,
    fieldName,
    Array.from(Array(numberOfStumps), (_, index) => ({
      value: (index + 1).toString(),
      label: (index + 1).toString(),
    })),
    invalidMessage,
  );
}

export function makeDateControl(
  label: string,
  fieldName: string,
  invalidMessage = "Input a valid date",
): JsonFormControl {
  const snPattern = /\d{2}-\d{2}-\d{4}$/;
  if (!snPattern) throw new Error(invalidMessage);
  const requiredConfig = {
    config: {
      pattern: {
        value: snPattern,
        message: invalidMessage,
      },
      required: invalidMessage,
      minLength: { value: 1, message: invalidMessage + " " + snPattern },
    },
  };

  return {
    label: label,
    inputType: "text",
    fieldName: fieldName,
    defaultValue: "",
    ...requiredConfig,
  };
}

/**
 * Makes a control to select an orientation (left or right)
 * @param label label for the orientation select
 * @param fieldName field name for the orientation select.
 * @param invalidMessage invalid message for the orietnation select
 */
export function makeOrientationSelectControl(
  label: string,
  fieldName: string,
  invalidMessage: string,
) {
  return makeSelectControl(label, fieldName, ["Left", "Right"], invalidMessage);
}

export function makeDestinationSelectControl(
  label: string,
  fieldName: string,
  final = false,
) {
  return makeSelectControl(
    label,
    fieldName,
    final ? MODULE_LOCATIONS_FINAL_LIST : MODULE_LOCATIONS_LIST,
    "Please select a destination",
  );
}

export function makeSerialNumberSelect(
  serialNumberType: PartType,
  label: string,
  fieldName: string,
  invalidMessage = "Please enter a valid serial number.",
  conditionalLogic?: ConditionalLogic[] | undefined,
  filterIncomplete?: boolean,
): JsonFormControl {
  return makeSerialNumberInput(
    serialNumberType,
    label,
    fieldName,
    false,
    invalidMessage,
    conditionalLogic,
    filterIncomplete,
  );
}

export function makeSerialNumberText(
  serialNumberType: PartType,
  label: string,
  fieldName: string,
  invalidMessage = "Please enter a valid serial number.",
) {
  return {
    ...makeSerialNumberInput(
      serialNumberType,
      label,
      fieldName,
      true,
      invalidMessage,
    ),
    autocomplete: "off",
  } as JsonFormControl;
}

/**
 * Makes a serial number input field with regex serial number validation.
 * @param {PartType} serialNumberType serial number part type expected to be entered.
 * @param {string} label UI facing name for this field.
 * @param {string} fieldName Database name for this field.
 * @param {string} placeholder Optional field placeholder text.
 * @param {string} invalidMessage Optional validation error message.
 * @returns {JsonFormControl}
 */
export function makeSerialNumberInput(
  serialNumberType: PartType,
  label: string,
  fieldName: string,
  asText = false,
  invalidMessage = "Please enter a valid serial number.",
  conditionalLogic?: ConditionalLogic[] | undefined,
  filterIncomplete?: boolean,
): JsonFormControl {
  const snPattern = PART_TYPE_REGEX_MAP[serialNumberType];
  if (!snPattern) throw new Error(`Invalid SN Type ${serialNumberType}`);
  const requiredConfig =
    invalidMessage === undefined || invalidMessage === ""
      ? {}
      : {
          config: {
            pattern: {
              value: snPattern,
              message: invalidMessage,
            },
            required: invalidMessage,
            minLength: { value: 1, message: invalidMessage + " " + snPattern },
          },
        };
  return {
    label,
    inputType: asText ? "text" : "select",
    fieldName,
    options: [],
    defaultValue: "",
    conditionalLogic: conditionalLogic,
    filterIncomplete,
    ...requiredConfig,
    controlConfigs: {
      serialNumberType,
    },
  };
}

export function makeAutocompleteControl(
  serialNumberType: PartType,
  label: string,
  fieldName: string,
  options: (string | { value: string; label: string })[],
  invalidMessage = "Please enter a valid serial number.",
  conditionalLogic?: ConditionalLogic[] | undefined,
): JsonFormControl {
  const snPattern = PART_TYPE_REGEX_MAP[serialNumberType];
  if (!snPattern) throw new Error(`Invalid SN Type ${serialNumberType}`);
  const requiredConfig =
    invalidMessage === undefined || invalidMessage === ""
      ? {}
      : {
          config: {
            pattern: {
              value: snPattern,
              message: invalidMessage,
            },
            required: invalidMessage,
            minLength: { value: 1, message: invalidMessage + " " + snPattern },
          },
        };

  return {
    label,
    inputType: "autocomplete",
    fieldName,
    options: options.map((opt) =>
      typeof opt === "string" ? { value: opt, label: opt } : opt,
    ),
    defaultValue: "",
    conditionalLogic: conditionalLogic,
    ...requiredConfig,
    controlConfigs: {
      serialNumberType,
    },
  };
}

export function makeSerialNumberAutoselect(
  serialNumberType: PartType,
  label: string,
  fieldName: string,
  invalidMessage = "Please enter a valid serial number.",
  conditionalLogic?: ConditionalLogic[] | undefined,
): JsonFormControl {
  const options = ["test3", "test4"];
  return makeAutocompleteControl(
    serialNumberType,
    label,
    fieldName,
    options,
    invalidMessage,
    conditionalLogic,
  );
}
